/**
 * CampfirePanel - UI for interacting with campfires
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { Structure } from '../building/Structure';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemRegistry } from '../items/ItemRegistry';
import { t, Localization } from '../systems/Localization';

export class CampfirePanel extends UIPanel {
  private campfire: Structure | null = null;
  private inventory: InventoryComponent | null = null;

  // Callbacks
  public onItemCooked: ((itemId: string) => void) | null = null;

  constructor() {
    super(0, 0, 280, 220);
    this.isDraggable = true;
  }

  show(): void {
    super.show();
    this.title = t('panel_campfire');
  }

  setCampfire(campfire: Structure): void {
    this.campfire = campfire;
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  protected onPanelClick(pos: Vector2): boolean {
    if (!this.campfire || !this.inventory || !this.campfire.campfire) return false;

    const buttonY = this.bounds.y + this.titleBarHeight + 15;
    const buttonHeight = 35;

    // Add Fuel button
    const fuelButtonX = this.bounds.x + 15;
    const fuelButtonY = buttonY + 60;
    if (pos.x >= fuelButtonX && pos.x <= fuelButtonX + 120 &&
        pos.y >= fuelButtonY && pos.y <= fuelButtonY + buttonHeight) {
      this.addFuel();
      return true;
    }

    // Cook Raw Meat button
    const cookButtonX = this.bounds.x + 145;
    const cookButtonY = buttonY + 60;
    if (pos.x >= cookButtonX && pos.x <= cookButtonX + 120 &&
        pos.y >= cookButtonY && pos.y <= cookButtonY + buttonHeight) {
      this.startCooking();
      return true;
    }

    // Collect Cooked button (buttonY + 60 for buttons row, +50 for cook section, +40 for collect = +150)
    const collectButtonX = this.bounds.x + 15;
    const collectButtonY = buttonY + 150;
    if (pos.x >= collectButtonX && pos.x <= collectButtonX + 250 &&
        pos.y >= collectButtonY && pos.y <= collectButtonY + buttonHeight) {
      this.collectCooked();
      return true;
    }

    return false;
  }

  private addFuel(): void {
    if (!this.campfire || !this.inventory) return;

    const woodCount = this.inventory.getItemCount('wood');
    if (woodCount > 0) {
      const toAdd = Math.min(woodCount, 5); // Add up to 5 wood at a time
      const added = this.campfire.addFuel(toAdd);
      if (added > 0) {
        this.inventory.removeItem('wood', added);
      }
    }
  }

  private startCooking(): void {
    if (!this.campfire || !this.inventory) return;

    const rawMeatCount = this.inventory.getItemCount('raw_meat');
    if (rawMeatCount > 0 && this.campfire.campfire?.isBurning) {
      // Add all raw meat to the fire at once
      const added = this.campfire.startCooking('raw_meat', rawMeatCount);
      if (added > 0) {
        this.inventory.removeItem('raw_meat', added);
      }
    }
  }

  private collectCooked(): void {
    if (!this.campfire || !this.inventory) return;

    const result = this.campfire.collectCookedItem();
    if (result) {
      const item = ItemRegistry.get(result.itemId);
      if (item) {
        this.inventory.addItem(item, result.quantity);
        this.onItemCooked?.(result.itemId);
      }
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    if (!this.campfire?.campfire) return;

    const cf = this.campfire.campfire;
    const startY = this.bounds.y + this.titleBarHeight + 15;

    // Status
    ctx.fillStyle = cf.isBurning ? '#ff6b35' : '#666';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(cf.isBurning ? `🔥 ${t('campfire_burning')}` : `❄️ ${t('campfire_extinguished')}`, this.bounds.x + 15, startY + 15);

    // Fuel bar
    ctx.fillStyle = '#aaa';
    ctx.font = '12px monospace';
    ctx.fillText(`${t('campfire_fuel')}: ${Math.ceil(cf.fuel)}s / ${cf.maxFuel}s`, this.bounds.x + 15, startY + 38);

    // Fuel bar visual
    const barX = this.bounds.x + 15;
    const barY = startY + 42;
    const barW = 250;
    const barH = 10;
    ctx.fillStyle = '#333';
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = cf.isBurning ? '#ff6b35' : '#666';
    ctx.fillRect(barX, barY, barW * (cf.fuel / cf.maxFuel), barH);
    ctx.strokeStyle = '#555';
    ctx.strokeRect(barX, barY, barW, barH);

    // Buttons row
    const buttonY = startY + 60;

    // Add Fuel button
    const hasWood = this.inventory?.getItemCount('wood') ?? 0;
    ctx.fillStyle = hasWood > 0 ? '#8b4513' : '#444';
    ctx.fillRect(this.bounds.x + 15, buttonY, 120, 35);
    ctx.strokeStyle = hasWood > 0 ? '#a0522d' : '#555';
    ctx.strokeRect(this.bounds.x + 15, buttonY, 120, 35);
    ctx.fillStyle = hasWood > 0 ? '#fff' : '#666';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`+ ${t('campfire_fuel')} (${hasWood})`, this.bounds.x + 75, buttonY + 22);

    // Cook Raw Meat button (can add more even if already cooking same item)
    const hasRawMeat = this.inventory?.getItemCount('raw_meat') ?? 0;
    const canCook = hasRawMeat > 0 && cf.isBurning && (!cf.cookingItem || cf.cookingItem === 'raw_meat');
    ctx.fillStyle = canCook ? '#e74c3c' : '#444';
    ctx.fillRect(this.bounds.x + 145, buttonY, 120, 35);
    ctx.strokeStyle = canCook ? '#c0392b' : '#555';
    ctx.strokeRect(this.bounds.x + 145, buttonY, 120, 35);
    ctx.fillStyle = canCook ? '#fff' : '#666';
    ctx.fillText(`${t('campfire_cook')} (${hasRawMeat})`, this.bounds.x + 205, buttonY + 22);

    // Cooking progress
    const cookY = buttonY + 50;
    ctx.fillStyle = '#aaa';
    ctx.font = '12px monospace';
    ctx.textAlign = 'left';

    if (cf.cookingItem && cf.rawQuantity > 0) {
      const itemInfo = Localization.getItem(cf.cookingItem);
      const itemName = itemInfo?.name || ItemRegistry.get(cf.cookingItem)?.name || cf.cookingItem;
      const rawQtyText = cf.rawQuantity > 1 ? ` (${cf.rawQuantity} left)` : '';
      ctx.fillText(`${t('campfire_cooking')}: ${itemName}${rawQtyText}`, this.bounds.x + 15, cookY + 15);

      // Progress bar for current item
      ctx.fillStyle = '#333';
      ctx.fillRect(barX, cookY + 20, barW, barH);
      ctx.fillStyle = '#f39c12';
      ctx.fillRect(barX, cookY + 20, barW * cf.cookingProgress, barH);
      ctx.strokeStyle = '#555';
      ctx.strokeRect(barX, cookY + 20, barW, barH);

      // Percentage
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.floor(cf.cookingProgress * 100)}%`, this.bounds.x + 140, cookY + 29);
    } else {
      ctx.fillText(`${t('campfire_cooking')}: -`, this.bounds.x + 15, cookY + 15);
    }

    // Collect button - shows cooked items ready to pick up
    const collectY = cookY + 40;
    const canCollect = cf.cookedQuantity > 0;
    const cookedItemInfo = Localization.getItem('cooked_meat');
    const cookedMeatName = cookedItemInfo?.name || 'Cooked Meat';
    const collectQtyText = cf.cookedQuantity > 1 ? ` x${cf.cookedQuantity}` : '';
    ctx.fillStyle = canCollect ? '#4ecca3' : '#444';
    ctx.fillRect(this.bounds.x + 15, collectY, 250, 35);
    ctx.strokeStyle = canCollect ? '#7dffcc' : '#555';
    ctx.strokeRect(this.bounds.x + 15, collectY, 250, 35);
    ctx.fillStyle = canCollect ? '#000' : '#666';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(canCollect ? `${cookedMeatName.toUpperCase()}${collectQtyText}` : '-', this.bounds.x + 140, collectY + 22);
  }
}
